/**
 * German translations — primary locale for the technical UI.
 * Mirrors the mockup copy from docs/HTML design /tech-dashboard/index.html.
 * Add new keys here AND in en.ts; types are derived from this file.
 */
export const de = {
  common: {
    today: 'Heute',
    back: 'Zurück',
    close: 'Schließen',
    open: 'Öffnen',
    pause: 'Pause',
    resume: 'Fortsetzen',
    start: 'Starten',
    complete: 'Fertigstellen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    signOut: 'Abmelden',
    signOutFailed: 'Abmeldung fehlgeschlagen — Sie sind weiterhin angemeldet. Verbindung prüfen und erneut versuchen.',
    signOutEverywhere: 'Überall sonst abmelden',
    signOutEverywhereHint: "Beendet alle anderen Sitzungen außer dieser – nützlich bei einem verlorenen Gerät oder einer Anmeldung an einem geteilten Rechner.",
    signOutEverywhereDone: 'Von {count} anderen Geräten abgemeldet.',
    signOutEverywhereNone: 'Es waren keine anderen Geräte angemeldet.',
    signOutEverywhereFailed: 'Andere Geräte konnten nicht abgemeldet werden. Bitte erneut versuchen.',
    profile: 'Mein Profil',
    switchToDark: 'Dunkles Design',
    switchToLight: 'Helles Design',
    notifications: 'Benachrichtigungen',
    menu: 'Menü',
  },
  confirm: {
    addPartTitle: 'Dieses Teil hinzufügen?',
    addPartCta: 'Hinzufügen',
    requestPartTitle: 'Dieses Teil anfordern?',
    requestPartCta: 'Anfrage senden',
    removePartTitle: 'Dieses Teil entfernen?',
    removePartCta: 'Entfernen',
    // Zwei verschiedene Zusagen: einen ganzen Service übernehmen heißt den
    // Auftrag übernehmen, einen Schritt übernehmen heißt bei fremder Arbeit
    // mithelfen. TODO(copy): Wortlaut noch nicht abgenommen.
    claimServiceTitle: 'Diesen Service übernehmen?',
    claimServiceCta: 'Übernehmen',
    claimTitle: 'Diesen Schritt übernehmen?',
    claimCta: 'Übernehmen',
    // Der ganze Auftrag: jeder freie Service und, wenn noch niemand ihn hat,
    // der Auftrag selbst. TODO(copy): Wortlaut noch nicht abgenommen.
    claimJobTitle: 'Diesen Auftrag übernehmen?',
    claimJobCta: 'Übernehmen',
  },
  nav: {
    today: 'Heute',
    inventory: 'Lager',
    services: 'Leistungen',
  },
  filters: {
    board: 'Mein Board',
    undispatched: 'Nicht zugeteilt',
    progress: 'Laufend',
    upcoming: 'Geplant',
    done: 'Erledigt',
    label: 'Aufträge filtern',
    emptyUndispatched: 'Nichts zu übernehmen.',
    emptyProgress: 'Gerade läuft nichts.',
    emptyUpcoming: 'Nichts für später geplant.',
    emptyDone: 'Noch nichts abgeschlossen.',
    doneCapped: 'Die 20 letzten werden angezeigt.',
    scheduledFor: 'Geplant für',
    finished: 'Abgeschlossen',
  },
  // Failure copy for an action that was optimistically shown and then had to
  // be taken back. DRAFT — written by Claude, not signed off. `rejected` is
  // only a fallback: a 4xx normally carries the server's own RFC 7807
  // `detail`, which is a sentence already written for this reader and is
  // preferred over anything invented here.
  errors: {
    offline: 'Keine Verbindung — die Änderung wurde nicht gespeichert.',
    server: 'Serverproblem — bitte erneut versuchen.',
    rejected: 'Die Aktion war nicht möglich.',
    retry: 'Erneut versuchen',
    dismiss: 'Schliessen',
  },
  home: {
    // Landmark-Label der Heute-Seite. War hartcodiertes Englisch auf einer
    // deutschen Oberfläche — Screenreader lesen es vor.
    regionLabel: 'Heutige Aufgaben',
    loading: 'Lädt…',
    errorPanel: 'Aufgabenliste konnte nicht geladen werden.',
    errorWorkOrder: 'Auftrag konnte nicht geladen werden.',
    openWO: 'Auftrag öffnen',
    // Group labels over the WO cards. "Deine Arbeit" = cards holding my
    // running or paused task; the second label only appears when both
    // groups are populated.
    yourWork: 'Deine Aufträge',
    // Aufträge, die noch niemand hat — zum Übernehmen.
    openJobs: 'Offene Aufträge',
    otherWork: 'Weitere Aufträge',
    doneToday: (n: number) => `Heute erledigt (${n})`,
    emptyTitle: 'Gerade ist Ruhe',
    emptySub: 'Keine offenen Aufträge. Neue Aufträge erscheinen hier, sobald die Verwaltung sie anlegt.',
    bottomBarLabelActive: 'Aktiver Auftrag',
    bottomBarLabelIdle: 'Als Nächstes',
    bottomBarLabelResume: 'Arbeit fortsetzen',
  },
  tasks: {
    // Home (do-list) — grouped by work order, vehicle-first.
    claimError: 'Aufgabe konnte nicht übernommen werden.',
    // Der eine Griff, den es noch gibt: den ganzen Auftrag nehmen.
    claimJob: 'Auftrag übernehmen',
    claimJobError: 'Auftrag konnte nicht übernommen werden.',
    // Die Einzel-Service-Übernahme, wieder eingeführt (2026-09-22): sobald
    // der Techniker an anderer Stelle im Auftrag schon beteiligt ist, bietet
    // eine offene Zeile das statt der ganzen Auftragsübernahme oben an.
    // TODO(copy): Wortlaut noch nicht abgenommen.
    takeService: 'Übernehmen',
    // Ein Auftrag, der ohne Leistungen dispatcht wurde (2026-09-22) — das
    // Büro trägt sie nach; keine "Übernehmen"-Aktion, keine Zeilen.
    // TODO(copy): Wortlaut noch nicht abgenommen.
    noServiceYet: 'Noch keine Leistung eingetragen — das Büro trägt sie nach.',
    statusOpen: 'Offen',
    statusPending: 'Wartet auf Freigabe',
    statusRejected: 'Abgelehnt',
    statusDone: 'Erledigt',
    // Task-first Today card (concept 1b).
    you: 'Du',
    running: 'Läuft',
    doneCount: (n: number) => `${n} erledigt`,
    moreOpen: (n: number) => `+${n} weitere offen`,
    est: (duration: string) => `gesch. ${duration}`,
    // Das Zeitbudget eines Auftrags — vereinbart, gearbeitet, übrig.
    // `worked` zählt ALLE Mechaniker, denn dafür wurde geschätzt; `own`
    // ist der eigene Anteil und niemals die Basis der Rechnung.
    budget: {
      agreed: 'gesch.',
      worked: 'gearbeitet',
      left: 'übrig',
      over: 'über',
      own: 'deine',
      running: 'Zeit läuft',
      paused: 'Zeit pausiert',
      idle: 'Nicht gestartet',
    },
    // Services + task states (WO → service → task).
    service: 'Service',
    yourTotal: 'deine Zeit',
    onIt: 'ist dran',
    yoursLogged: (time: string) => `deine · ${time} erfasst`,
    pause: 'Pause',
    resume: 'Fortsetzen',
    awaitingCustomer: 'wartet auf Kundenfreigabe',
    approval: 'Freigabe',
    part: 'Teil',
    searchParts: 'Teile suchen…',
    start: 'Starten',
    claimedNotStarted: 'übernommen, nicht gestartet',
    // WO detail — task cards.
    sectionTitle: 'Aufgaben',
    empty: 'Keine Aufgaben auf diesem Auftrag.',
    loadError: 'Aufgaben konnten nicht geladen werden.',
    markDone: 'Erledigt',
    markDoneError: 'Aufgabe konnte nicht abgeschlossen werden.',
    estimate: (price: string, duration: string) => `geschätzt ${price} · ${duration}`,
    // Propose-task modal ("Zusätzliche Arbeit").
    propose: 'Zusätzliche Arbeit',
    proposeHint: 'Wird der Verwaltung zur Kundenfreigabe gemeldet. Erst nach Freigabe bearbeitbar.',
    proposeTitleLabel: 'Titel',
    proposeTitlePlaceholder: 'z. B. Bremsbeläge vorne erneuern',
    proposeNoteLabel: 'Notiz (optional)',
    proposeServicesLabel: 'Leistungen aus dem Katalog (optional)',
    proposeSubmit: 'Anfrage senden',
    proposeCancel: 'Abbrechen',
    proposeError: 'Anfrage konnte nicht gesendet werden.',
  },
  login: {
    pageTitle: 'Anmelden',
    emailLabel: 'E-Mail',
    passwordLabel: 'Passwort',
    forgotPassword: 'Passwort vergessen?',
    submit: 'Anmelden',
    submitting: 'Wird angemeldet…',
    invalidCredentials: 'E-Mail oder Passwort ist falsch.',
    genericError: 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    invalidEmailFormat: 'Ungültige E-Mail-Adresse',
    bannerInvalidCredentials: 'Ungültige Anmeldedaten',
    bannerNetworkError: 'Verbindung fehlgeschlagen',
    continueWithGoogle: 'Mit Google fortfahren',
    oauthDivider: 'oder',
    bannerOauthCancelled: 'Google-Anmeldung abgebrochen',
    bannerOauthFailed: 'Google-Anmeldung fehlgeschlagen',
    // TODO(copy)
    bannerOauthUnknownAccount:
      'Dieses Google-Konto ist für Vollgas nicht eingerichtet. Bitte wende dich an eine:n Administrator:in.',
  },
  healthGate: {
    serverUnavailable: 'Server nicht erreichbar',
    serverUnavailableHint: 'Wir können den Werkstatt-Server gerade nicht erreichen. Bitte erneut versuchen.',
    retry: 'Erneut versuchen',
    slow: [
      'Wir machen die Werkstatt startklar…',
      'Lager wird geprüft…',
      'Schraubenschlüssel werden poliert…',
      'Letzter Schluck Kaffee…',
      'Termine werden sortiert…',
    ] as ReadonlyArray<string>,
  },
  workOrder: {
    // Landmark-Label der Auftragsdetailseite — war hartcodiertes Englisch.
    regionLabel: 'Auftragsdetails',
    mileageAtStart: 'km-Stand bei Beginn',
    mileageAtComplete: 'km-Stand bei Abschluss',
    completed: 'Abgeschlossen',
    timeLogged: 'Erfasste Zeit',
    timerRunning: 'Läuft',
    timerPaused: 'Pausiert',
    timerStarted: (time: string, day: string) => `Gestartet ${time} · ${day}`,
    manualTimeEntry: 'Timer vergessen? Zeit manuell erfassen',
    addNote: 'Notiz hinzufügen…',
    // TODO(copy): placeholder wording for the chat thread (2026-09-22).
    sendNote: 'Senden',
    noNotes: 'Noch keine Notizen.',
    noteError: 'Notiz konnte nicht gespeichert werden.',
    notePreview: 'Vorschau',
    quickNoteFoilingDone: 'Folierung fertig',
    quickNoteBrakesOk: 'Bremsen OK',
    parts: 'Teile',
    partRequestPending: 'angefragt · wartet auf die Werkstattleitung',
    partRequestAccepted: 'genehmigt · Leitung trägt das Teil ein',
    partRequestDeclined: 'abgelehnt',
    partRequestWithdrawn: 'zurückgezogen',
    withdrawPartRequest: 'Zurückziehen',
    partsTotal: 'Teile gesamt',
    completeWork: 'Arbeit abschließen',
    // TODO(copy): placeholder wording — needs Roham's verbatim text.
    handedBack: 'Zurückgegeben — der Serviceberater prüft den Auftrag. Hier ist nichts mehr zu tun.',
    reopenWork: 'Arbeit erneut öffnen',
    // WO detail (concept 1a) — accordion.
    done: 'erledigt',
    show: 'zeigen',
    km: 'km',
    sku: 'SKU',
    logTimeManually: 'Zeit manuell erfassen',
    finishTask: 'Service abschließen',
    addPart: 'Teil',
    claimToStart: 'Auftrag übernehmen, um zu starten.',
    partsCount: (n: number) => `${n} ${n === 1 ? 'Teil' : 'Teile'}`,
    allTasksDone: (time: string) => `Alle Services erledigt — ${time} gesamt`,
    completeHint: 'Arbeit abschließen gibt den Auftrag zur Prüfung an die Beratung zurück.',
    // Der Auftrag als Ganzes: wem er gehört, was der Kunde gesagt hat.
    jobNotes: 'Notizen zum Auftrag',
    // Confirmed by the user 2026-09-22.
    newNoteBadge: 'Neue Notiz zum Auftrag',
    assignedTo: 'Zugeteilt an',
    unassigned: 'Noch niemandem zugeteilt',
    noDescription: 'Keine Beschreibung von der Beratung.',
    description: 'Beschreibung',
    // Siehe en.ts: nur noch generischer Fallback.
    completeError: 'Auftrag konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
    // TODO(copy): placeholder wording — needs Roham's verbatim text.
    completeErrEmpty: 'Für diesen Auftrag ist noch nichts erfasst — Zeit erfassen, ein Teil hinzufügen oder die erbrachte Leistung eintragen.',
    completeErrForbidden: 'Sie sind diesem Auftrag nicht zugeteilt und können ihn daher nicht zurückgeben. Bitte den Werkstattleiter um Zuteilung.',
    // TODO(copy): Figma-Wording steht noch aus.
    proposalPending: 'Eine vorgeschlagene Arbeit wartet noch auf den Kunden. Sie hält das Fahrzeug nicht auf — die Beratung entscheidet.',
    completeBlockedByTasks: 'Der Auftrag hat inzwischen neue Arbeit bekommen — offene Services erledigen, dann zurückgeben.',
    additionalService: 'Zusätzlicher Service',
    removePart: 'Entfernen',
    saved: 'Gespeichert',
    saveFailed: 'Speichern fehlgeschlagen',
  },
  inventory: {
    title: 'Lager',
    subtitle: 'Lagerbestand der Werkstatt.',
    search: 'Teile suchen…',
    onHand: 'Bestand',
    reorder: 'Meldebestand',
    lowStock: 'Niedrig',
    empty: 'Keine Teile gefunden.',
    loadError: 'Lager konnte nicht geladen werden.',
    unitPrice: 'Einzelpreis',
  },
  reorder: {
    button: 'Material nachbestellen',
    title: 'Material nachbestellen',
    hint: 'Melde dem Management, welche Materialien aufgebraucht sind und nachbestellt werden müssen.',
    searchPlaceholder: 'Material suchen oder neu eingeben…',
    addNew: (name: string) => `„${name}“ als neues Material anlegen`,
    newBadge: 'Neu',
    listLabel: 'Zu bestellende Materialien',
    empty: 'Noch keine Materialien hinzugefügt.',
    quantity: 'Menge',
    notePlaceholder: 'Notiz (optional) — z. B. Marke, Größe',
    remove: 'Entfernen',
    submit: 'An Management senden',
    submitting: 'Wird gesendet…',
    cancel: 'Abbrechen',
    success: 'Nachbestellung ans Management gesendet.',
    error: 'Senden fehlgeschlagen. Bitte erneut versuchen.',
  },
  manualLabour: {
    title: 'Zeit manuell erfassen',
    hint: 'Timer vergessen? Trage die gearbeitete Zeitspanne ein — sie zählt zu dieser Aufgabe, genau wie der Live-Timer.',
    start: 'Beginn',
    end: 'Ende',
    addBreak: 'Pause hinzufügen',
    breakStart: 'Pausenbeginn',
    breakEnd: 'Pausenende',
    submit: 'Zeit erfassen',
    errorSpan: 'Beginn und Ende eingeben.',
    errorOrder: 'Ende muss nach dem Beginn liegen.',
    errorBreak: 'Pausenzeiten prüfen.',
    errorSubmit: 'Zeit konnte nicht erfasst werden. Bitte erneut versuchen.',
  },
  proposeService: {
    title: 'Zusätzliche Arbeit vorschlagen',
    hint: 'Mehr entdeckt, das gemacht werden muss? Dies erstellt eine Aufgabe, die der Manager mit dem Kunden abklärt, bevor sie bearbeitet werden kann.',
    workTitle: 'Was zu tun ist',
    workTitlePlaceholder: 'z. B. Vordere Bremsscheiben ersetzen',
    service: 'Service (optional)',
    noService: 'Kein Katalog-Service',
    note: 'Notiz (optional)',
    notePlaceholder: 'Was die Beratung dem Kunden sagen soll',
    submit: 'Arbeit vorschlagen',
    errorTitle: 'Beschreibe zuerst die Arbeit.',
    errorSubmit: 'Arbeit konnte nicht vorgeschlagen werden. Bitte erneut versuchen.',
  },
  walkin: {
    title: 'Laufkunde',
    subtitle: 'Auftrag ohne Termin anlegen.',
    stepCustomer: 'Kunde',
    stepVehicle: 'Fahrzeug',
    stepService: 'Leistung',
    searchCustomer: 'Kunde suchen (Name, Telefon)…',
    noCustomers: 'Keine Kunden gefunden.',
    newCustomerUnavailable: 'Neuanlage von Laufkunden ist noch nicht verfügbar — bitte einen bestehenden Kunden suchen oder im Büro anlegen lassen.',
    selectVehicle: 'Fahrzeug wählen',
    noVehicles: 'Keine Fahrzeuge für diesen Kunden.',
    addVehicle: 'Fahrzeug hinzufügen',
    plate: 'Kennzeichen',
    make: 'Marke',
    model: 'Modell',
    year: 'Baujahr',
    saveVehicle: 'Fahrzeug speichern',
    mileageAtIntake: 'km-Stand bei Annahme (optional)',
    startWork: 'Arbeit starten',
    back: 'Zurück',
    next: 'Weiter',
    createError: 'Auftrag konnte nicht angelegt werden.',
    mileageInvalid: 'Bitte einen gültigen km-Stand eingeben (0 oder mehr).',
    yearInvalid: 'Bitte ein gültiges Baujahr eingeben.',
  },
  addPart: {
    title: 'Teil hinzufügen',
    search: 'Katalog durchsuchen…',
    empty: 'Keine Teile gefunden.',
    quantity: 'Menge',
    add: 'Hinzufügen',
    cancel: 'Abbrechen',
    addError: 'Teil konnte nicht hinzugefügt werden.',
    addNew: 'Neues Teil hinzufügen',
    taskTarget: 'Aufgabe',
  },
  otherLine: {
    title: 'Andere Position',
    hint: 'Für Teile, die nicht im Katalog stehen. Wird zur Prüfung an die Verwaltung markiert.',
    description: 'Beschreibung',
    descriptionPlaceholder: 'z. B. Spezialdichtung',
    unitPrice: 'Einzelpreis (€)',
    quantity: 'Menge',
    vatRate: 'USt.-Satz (%)',
    add: 'Position hinzufügen',
    cancel: 'Abbrechen',
    addError: 'Position konnte nicht hinzugefügt werden.',
  },
  complete: {
    title: 'Arbeit abschließen',
    review: 'Bitte Notizen, Zeit und Teile prüfen. Abschluss bestätigen?',
    timeLogged: 'Erfasste Zeit',
    partsTotal: 'Teile gesamt',
    notesCount: (n: number) => `${n} ${n === 1 ? 'Notiz' : 'Notizen'}`,
    mileagePrompt: 'km-Stand jetzt? (leer lassen zum Überspringen)',
    confirm: 'Abschluss bestätigen',
    cancel: 'Abbrechen',
    completeError: 'Abschluss fehlgeschlagen.',
    autoInvoice: 'Ein Rechnungsentwurf wird automatisch für die Verwaltung erstellt.',
  },
  manualTime: {
    title: 'Zeit manuell erfassen',
    date: 'Datum',
    workPeriod: 'Arbeitszeit',
    pauses: 'Pausen',
    addPause: 'Pause hinzufügen',
    noPauses: 'Keine Pausen',
    removePause: 'Pause entfernen',
    worked: 'Erfasst',
    breakTotal: 'Pause',
    add: 'Zeit hinzufügen',
    cancel: 'Abbrechen',
    addError: 'Zeit konnte nicht erfasst werden.',
    invalidRange: 'Ende muss nach dem Beginn liegen.',
  },
  reopen: {
    title: 'Arbeit erneut öffnen',
    hint: 'Innerhalb von 1 Stunde nach Abschluss möglich. Der Grund wird protokolliert.',
    reasonLabel: 'Grund',
    reasonPlaceholder: 'Warum wird die Arbeit erneut geöffnet?',
    submit: 'Erneut öffnen',
    cancel: 'Abbrechen',
    error: 'Arbeit konnte nicht erneut geöffnet werden.',
  },
  partRequest: {
    title: 'Teil anfragen',
    hint: 'Teile werden von der Verwaltung freigegeben — Anfrage wird geprüft.',
    partLabel: 'Teil',
    partPlaceholder: 'z. B. Bremsbeläge vorne',
    quantity: 'Menge',
    note: 'Notiz (optional)',
    submit: 'Anfrage senden',
    submitError: 'Anfrage konnte nicht gesendet werden.',
    sent: 'Anfrage gesendet — die Verwaltung prüft sie.',
    another: 'Weitere Anfrage',
  },
  services: {
    title: 'Leistungen',
    subtitle: 'Leistungskategorien der Werkstatt.',
    search: 'Leistungen suchen…',
    empty: 'Keine Leistungen gefunden.',
    loadError: 'Leistungen konnten nicht geladen werden.',
  },
  notifications: {
    title: 'Benachrichtigungen',
    empty: 'Keine neuen Benachrichtigungen.',
    loadError: 'Benachrichtigungen konnten nicht geladen werden.',
    dismiss: 'Ausblenden',
    dismissAll: 'Alle ausblenden',
  },
  profile: {
    title: 'Mein Profil',
    role: 'Rolle',
    email: 'E-Mail',
    language: 'Sprache',
    password: 'Passwort',
    changePassword: 'Passwort ändern',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Neues Passwort wiederholen',
    show: 'Anzeigen',
    hide: 'Verbergen',
    errLength: 'Das Passwort muss mindestens 8 Zeichen haben.',
    errMismatch: 'Die beiden neuen Passwörter stimmen nicht überein.',
    errCurrentWrong: 'Das aktuelle Passwort ist falsch.',
    sessionHint: 'Hier bleibst du angemeldet. Andere Tablets werden abgemeldet.',
    saving: 'Wird gespeichert…',
    errGeneric: 'Passwort konnte nicht geändert werden. Bitte erneut versuchen.',
    success: 'Passwort geändert.',
  },
}

/**
 * Shape derived from `de` (the source-of-truth dictionary). String values
 * are intentionally widened to `string` so `en.ts` can satisfy the same
 * shape without TS demanding identical literal types.
 */
type Widen<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => R
    : T extends object
      ? { [K in keyof T]: Widen<T[K]> }
      : T

export type Translations = Widen<typeof de>
